<script>import { createEventDispatcher, getContext, onMount } from "svelte";
import { v4 as uuid } from "uuid";
import { formReset } from "../../../actions/formReset.js";
export let options;
export let contextKey;
const ctx = getContext(contextKey);
let { name, empty, dirty, focus, visited, disabled, value } = ctx;
let entries = options ? options.map(
  (option) => Object.assign({ id: uuid(), checked: option.value === $value }, option)
) : [];
$empty = entries.filter((option) => option.checked).length === 0;
if ($empty) {
  entries.unshift({ value: "", label: "", id: uuid(), checked: $empty });
}
let startValue = !$empty ? $value : "";
const setInitialValue = $value === void 0 || startValue !== $value;
let initialValue = $value;
let state = {
  empty: $empty,
  dirty: $dirty,
  focus: $focus,
  visited: $visited,
  disabled: $disabled
};
const dispatch = createEventDispatcher();
const getLabel = (value2) => entries.filter((option) => option.value === value2).map((option) => option.label).join(", ");
let label = getLabel($value);
$: {
  $empty = $value === void 0;
  $dirty = !$disabled ? ($value !== void 0 || !initialValue != void 0) && $value !== initialValue : false;
  $focus = !$disabled ? $focus : false;
  $visited = !$disabled ? $visited : false;
  let changes = {};
  if ($empty != state.empty) {
    changes.empty = $empty;
  }
  if ($dirty != state.dirty) {
    changes.dirty = $dirty;
  }
  if ($focus != state.focus) {
    changes.focus = $focus;
  }
  if ($visited != state.visited) {
    changes.visited = $visited;
  }
  state = { empty: $empty, dirty: $dirty, focus: $focus, visited: $visited, disabled: $disabled };
  if (Object.keys(changes).length > 0) {
    dispatch("state", { changes, state });
  }
}
let self;
onMount(() => {
  if (setInitialValue && !$disabled) {
    $value = startValue;
    dispatch("changed", {
      name,
      value: $value
    });
  }
});
const onInputEvent = (ev) => {
  const elem = ev.target ? ev.target : void 0;
  if (elem) {
    const emptyEntryShouldBeChecked = !$value && elem.value === "" && !elem.checked;
    if (emptyEntryShouldBeChecked) {
      elem.checked = true;
    }
    const dispatchChangedValue = elem.checked && elem.value !== $value || !elem.checked && elem.value === $value && $value;
    const newValue = elem.checked ? elem.value : elem.value === $value ? "" : $value;
    $value = elem.checked ? elem.value : elem.value === $value ? "" : $value;
    label = getLabel($value);
    if (dispatchChangedValue) {
      dispatch("changed", {
        name,
        value: $value
      });
    }
  } else {
  }
};
const focusComponent = (ev) => {
  self.focus();
};
const handleExternalValue = (externalValue) => {
  entries.forEach((option) => {
    option.checked = option.value === externalValue;
  });
  $empty = entries.filter((option) => option.checked).length === 0;
  if ($empty) {
    entries[0].checked = true;
  }
  initialValue = externalValue;
  label = getLabel(externalValue);
  entries = entries;
};
$: {
  handleExternalValue($value);
}
</script>

<div
	bind:this={self}
	class="select"
	on:focus={() => {
		if (!$disabled) {
			$visited = true;
			$focus = true;
		} else {
			self.blur();
		}
	}}
	on:blur={() => {
		$focus = false;
	}}
	tabindex="-1"
>
	<span class="entries">
		<input {name} type="hidden" use:formReset value={$value} />
		<input
			class="select"
			type="text"
			use:formReset
			value={label}
			on:focus={focusComponent}
			disabled={$disabled}
		/>
		{#each entries as entry}
			<input
				class="entry"
				id={entry.id}
				type="radio"
				use:formReset
				bind:group={$value}
				value={entry.value}
				on:input={onInputEvent}
				checked={entry.value === $value}
			/>
			<label for={entry.id}
				><span class="text"
					>{#if !!entry.label}{entry.label}{:else}&nbsp;{/if}</span
				></label
			>
		{/each}
	</span>
	<span class="toggle {!$disabled ? 'active' : ''}">
		<span class="glyph-chevron-down" />
	</span>
</div>

<style>
	div.select {
		--option-hover-brightness: 0.8;
	}

	div.select {
		display: flex;
		max-height: calc(1em * 1.2);
		position: relative;
	}
	div.select span.toggle {
		flex-grow: 0;
		flex-shrink: 0;
		display: none;
	}
	div.select span.toggle.active {
		cursor: pointer;
		display: inline;
	}
	div.select span.entries input.select {
		padding: 0;
		outline: none;
		border: 0;
		background-color: transparent;
		color: var(--text-color);
		font: var(--input-font);
		letter-spacing: var(--input-letter-spacing);
		line-height: 1.2;
		z-index: 1;
	}
	div.select span.entries input.select:not(:disabled) {
		cursor: pointer;
	}

	div.select span.entries input.entry {
		position: absolute;
		opacity: 0;
	}

	div.select span.entries {
		flex-grow: 1;
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		position: relative;
		z-index: 0;
		max-width: calc(100% - var(--input-font-size));
	}
	div.select:focus span.entries {
		z-index: 1;
	}

	div.select span.entries label {
		background-color: var(--fill);
		line-height: 1.25;
		padding: calc(var(--input-text-margin-bottom) / 2) 1rem;
		border-bottom: 1px solid var(--border-color);
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 0.1s;
	}

	div.select span.entries label:nth-child(4) {
		border-top: 1px solid var(--border-color);
	}

	div.select span.entries label:hover {
		filter: brightness(var(--option-hover-brightness));
	}

	div.select:not(:focus) span.entries label {
		opacity: 0;
		height: 0px;
		padding: 0 1rem;
		border: 0;
	}

	div.select span.entries input.entry:checked + label {
		background-color: var(--focus-color);
		color: var(--fill);
	}
</style>
